/**
 * Article → member practices.
 *
 * The article itself stays free and publicly indexable. The guided practices it
 * points to are part of the Recovery Trauma™ membership.
 *
 * Structure: Google / homepage → free article → learn → relevant practices (members)
 * → join Recovery Trauma™.
 *
 * Every article gets at least two practices that genuinely match the topic.
 * Articles without an explicit entry fall back to a category-level pairing, so
 * future articles are covered automatically.
 */

export type ArticlePractice = {
  /** Member-only destination inside the platform. */
  to: string;
  /** Route params, when the destination is a single practice page. */
  params?: Record<string, string>;
  /** Search params, when the destination opens one exercise inside a hub. */
  search?: Record<string, string>;
  title: string;
  /** Why this practice may help in relation to what was just read. */
  blurb: string;
};

/**
 * Link props for a practice card. Members go straight to the exercise itself;
 * everyone else is invited to join.
 */
export function practiceLinkProps(practice: ArticlePractice, hasAccess: boolean) {
  if (!hasAccess) return { to: "/choose-membership" } as Record<string, unknown>;
  return {
    to: practice.to,
    ...(practice.params ? { params: practice.params } : {}),
    ...(practice.search ? { search: practice.search } : {}),
  } as Record<string, unknown>;
}


/** Catalogue of practices an article may recommend. */
export const PRACTICE_LIBRARY = {
  grounding: {
    to: "/grounding",
    title: "Grounding practice",
    blurb:
      "Bring your attention back to the present when your body reacts faster than your thinking can keep up.",
  },
  "extended-exhale": {
    to: "/breathing/core/$slug",
    params: { slug: "extended-exhale" },
    title: "Extended Exhale Breathing",
    blurb:
      "A longer out-breath gently signals safety to a nervous system that is running hot.",
  },
  "box-breathing": {
    to: "/breathing/core/$slug",
    params: { slug: "box-breathing" },
    title: "Box Breathing",
    blurb:
      "An even, predictable rhythm to steady yourself when anxiety or overwhelm builds.",
  },
  "panic-reset": {
    to: "/breathing/core/$slug",
    params: { slug: "panic-reset" },
    title: "Panic Reset breathing",
    blurb:
      "A short practice for the moments when everything speeds up at once.",
  },
  breathing: {
    to: "/breathing",
    title: "Guided breathing practices",
    blurb:
      "A small library of breathing exercises you can match to how you feel today.",
  },
  somatic: {
    to: "/somatic",
    title: "Somatic regulation practices",
    blurb:
      "Slow body-based movements for releasing what stays held in the body after stress.",
  },
  tapping: {
    to: "/tapping",
    title: "EFT Tapping practice",
    blurb:
      "A guided tapping sequence for settling strong feelings without having to explain them.",
  },
  "eft-flashcards": {
    to: "/eft-flashcards",
    title: "EFT tapping flashcards",
    blurb:
      "Short prompts you can follow point by point, at your own pace.",
  },
  bilateral: {
    to: "/bilateral",
    title: "Bilateral stimulation practice",
    blurb:
      "Gentle left-right stimulation, the same principle used alongside EMDR-style work.",
  },
  "regulation-compass": {
    to: "/regulation-compass",
    title: "The Regulation Compass",
    blurb:
      "Find where your nervous system is right now — and a practice matched to that state.",
  },
  "nervous-system-reset": {
    to: "/nervous-system-reset",
    title: "Reset & Regulate — 30-Day Nervous System Regulation Journey",
    blurb:
      "Small daily practices, self-paced, no streaks and no deadlines.",
  },
  "breaking-free": {
    to: "/breaking-free",
    title: "Breaking Free — 30-Day Trauma Bond Recovery Journey",
    blurb:
      "Day by day support for loosening a bond that still pulls, with everything you write kept private.",
  },
  "secure-within": {
    to: "/secure-within",
    title: "Secure Within — 30-Day Attachment Healing Journey",
    blurb:
      "Understand your attachment patterns and practise more secure ways of connecting.",
  },
  journal: {
    to: "/journal",
    title: "The Recovery Journal",
    blurb:
      "Gentle, compassionate prompts for putting words to what you have just been reading about.",
  },
  "trigger-journal": {
    to: "/trigger-journal",
    title: "The Trigger Journal",
    blurb:
      "Track what set a reaction off, what your body did, and what you needed — privately.",
  },
  "inner-child": {
    to: "/inner-child",
    title: "Inner Child practice",
    blurb:
      "A guided, trauma-informed practice for meeting the child you once were with kindness.",
  },
  "shadow-work": {
    to: "/shadow-work",
    title: "Shadow Work reflection",
    blurb:
      "Meet the parts of you that learned to stay hidden, without judgement.",
  },
  "know-your-patterns": {
    to: "/know-your-patterns",
    title: "Know Your Patterns",
    blurb:
      "Notice the relationship patterns that repeat, so they become easier to recognise early.",
  },
  "abuse-journal": {
    to: "/abuse-journal",
    title: "The private incident journal",
    blurb:
      "A secure, private place to record what happened in your own words, for your own clarity.",
  },
  "dissociation-journal": {
    to: "/dissociation-journal",
    title: "Dissociation Journal",
    blurb:
      "Notice when you drift, what came before it, and what helps you come back.",
  },
  "safety-stabilisation": {
    to: "/safety-stabilisation",
    title: "Safety & Stabilisation practices",
    blurb:
      "The first-phase practices that build steadiness before deeper work.",
  },
  "dbt-skills": {
    to: "/dbt-skills",
    title: "DBT distress tolerance skills",
    blurb:
      "Practical skills for riding out intense feelings without acting on them.",
  },
  "cbt-thought-cards": {
    to: "/cbt-thought-cards",
    title: "CBT thought cards",
    blurb:
      "Work through a harsh thought step by step and find a kinder, truer version.",
  },
  "acts-of-self-care": {
    to: "/acts-of-self-care",
    title: "Small acts of self-care",
    blurb:
      "Very small, doable steps for days when motivation is not available.",
  },
  "self-love": {
    to: "/self-love",
    title: "Self-compassion practice",
    blurb:
      "Practise speaking to yourself the way you would speak to someone you care about.",
  },
  sleep: {
    to: "/sleep",
    title: "Sleep & rest practices",
    blurb:
      "Wind-down practices for a body that struggles to let its guard down at night.",
  },
  meditations: {
    to: "/meditations",
    title: "Guided meditations",
    blurb:
      "Short guided audio for settling, without needing to sit still perfectly.",
  },

  // ── Single exercises: these open the practice itself, not a list ──
  "grounding-54321": {
    to: "/grounding",
    search: { open: "5-4-3-2-1" },
    title: "5-4-3-2-1 Senses grounding",
    blurb:
      "Move gently through your five senses to bring your attention back to where you actually are.",
  },
  "sensory-grounding": {
    to: "/grounding",
    search: { open: "sensory-grounding" },
    title: "Sensory Grounding",
    blurb:
      "Touch, sound and sight give your attention something concrete to rest on when everything feels loud inside.",
  },
  "safe-place": {
    to: "/grounding",
    search: { open: "safe-place" },
    title: "Safe Place visualisation",
    blurb:
      "Build a quiet inner place you can return to when the present feels unsafe.",
  },
  "feet-on-ground": {
    to: "/grounding",
    search: { open: "feet-on-ground" },
    title: "Feet On The Ground",
    blurb:
      "A two-minute anchor through the soles of your feet, for moments of drifting or flashback.",
  },
  "body-scan": {
    to: "/grounding",
    search: { open: "body-scan" },
    title: "Full Body Scan",
    blurb:
      "Move attention slowly through the body to notice what is held, without needing to change it.",
  },
  "progressive-relaxation": {
    to: "/grounding",
    search: { open: "progressive" },
    title: "Progressive Relaxation",
    blurb:
      "Tense and release each area in turn, so the body has a way to let stress go.",
  },
  "belly-breathing": {
    to: "/breathing/core/$slug",
    params: { slug: "belly-breathing" },
    title: "Belly Breathing",
    blurb:
      "Slow, low breathing that helps the body come out of a braced, chest-high pattern.",
  },
  "4-7-8-breathing": {
    to: "/breathing/core/$slug",
    params: { slug: "4-7-8" },
    title: "4-7-8 Breathing",
    blurb:
      "A longer out-breath rhythm often used to settle before sleep or after a spike of anxiety.",
  },
  "self-havening": {
    to: "/somatic/$practice",
    params: { practice: "self-havening" },
    title: "Self-Havening",
    blurb:
      "Slow self-touch on the arms and face, for soothing yourself without having to explain anything.",
  },
  "voo-breath": {
    to: "/somatic/$practice",
    params: { practice: "voo-breath" },
    title: "Voo Breath",
    blurb:
      "A low humming breath that works through the body rather than the thinking mind.",
  },
  "somatic-orienting": {
    to: "/somatic/$practice",
    params: { practice: "orienting" },
    title: "Orienting practice",
    blurb:
      "Let your eyes travel around the room so your body can check, slowly, that you are here now.",
  },
  "butterfly-hug": {
    to: "/somatic/$practice",
    params: { practice: "butterfly-hug" },
    title: "Butterfly Hug",
    blurb:
      "Gentle alternating taps across the chest — steadying when feelings arrive faster than words.",
  },
  "hand-on-heart": {
    to: "/somatic/$practice",
    params: { practice: "hand-on-heart" },
    title: "Hand On Heart",
    blurb:
      "A small, private gesture of self-contact for moments of shame or loneliness.",
  },
  container: {
    to: "/somatic/$practice",
    params: { practice: "container" },
    title: "The Container practice",
    blurb:
      "Put something heavy down for now, deliberately, so it does not have to be carried all day.",
  },
  pendulation: {
    to: "/somatic/$practice",
    params: { practice: "pendulation" },
    title: "Pendulation",
    blurb:
      "Move attention between what feels difficult and what feels okay, so nothing overwhelms.",
  },
  resourcing: {
    to: "/somatic/$practice",
    params: { practice: "resourcing" },
    title: "Resourcing",
    blurb:
      "Build a felt sense of something steady before approaching anything harder.",
  },
  "protective-boundary": {
    to: "/somatic/$practice",
    params: { practice: "protective-boundary" },
    title: "Protective Boundary",
    blurb:
      "A body-based practice for sensing where you end and someone else begins.",
  },
  "tre-shake": {
    to: "/somatic/$practice",
    params: { practice: "tre-shake" },
    title: "Shaking practice",
    blurb:
      "Let the body discharge what it has been holding, the way animals do after a threat passes.",
  },
  "wall-push": {
    to: "/somatic/$practice",
    params: { practice: "wall-push" },
    title: "Wall Push",
    blurb:
      "Give anger or defensive energy somewhere safe and physical to go.",
  },
  "legs-up-the-wall": {
    to: "/somatic/$practice",
    params: { practice: "legs-up-the-wall" },
    title: "Legs Up The Wall",
    blurb:
      "A restorative pose for tiredness, heaviness and a nervous system that needs rest.",
  },
  "grounding-movement": {
    to: "/somatic/$practice",
    params: { practice: "grounding-movement" },
    title: "Grounding Movement",
    blurb:
      "Slow, weighted movement for coming back into the body when you feel far away.",
  },
  "meditation-grounding": {
    to: "/meditations",
    search: { open: "two-min-grounding" },
    title: "Two-minute grounding meditation",
    blurb:
      "A very short guided audio for when sitting still for long feels impossible.",
  },
  "meditation-safety": {
    to: "/meditations",
    search: { open: "finding-safety" },
    title: "Finding Safety meditation",
    blurb:
      "A guided practice for building a felt sense of safety, at your own pace.",
  },
  "meditation-calm-ns": {
    to: "/meditations",
    search: { open: "calming-ns" },
    title: "Calming the nervous system",
    blurb:
      "Guided audio for settling a body that has been running on alert.",
  },
  "meditation-sleep": {
    to: "/meditations",
    search: { open: "before-sleep" },
    title: "Before Sleep meditation",
    blurb:
      "A wind-down practice for nights when the body will not let its guard down.",
  },
  "meditation-self-compassion": {
    to: "/meditations",
    search: { open: "self-compassion" },
    title: "Self-compassion meditation",
    blurb:
      "Practise meeting yourself with the warmth you would offer someone you love.",
  },
} as const satisfies Record<string, ArticlePractice>;


export type PracticeId = keyof typeof PRACTICE_LIBRARY;

/** Explicit, topic-matched practices per article slug (always at least two). */
export const ARTICLE_PRACTICE_IDS: Record<string, PracticeId[]> = {
  // Trauma recovery
  "100-signs-of-childhood-trauma-in-adults": ["inner-child", "journal"],
  "4-trauma-responses-fight-flight-freeze-fawn": ["grounding-54321", "extended-exhale"],
  "aaghat-se-ubharna-pehla-kadam": ["grounding-54321", "nervous-system-reset"],
  "adhd-and-trauma": ["regulation-compass", "somatic-orienting"],
  "anhedonia-cant-feel-pleasure-trauma": ["acts-of-self-care", "journal"],
  "autism-and-trauma": ["regulation-compass", "sensory-grounding"],
  "beginners-guide-to-trauma-recovery": ["nervous-system-reset", "journal"],
  "can-trauma-be-overcome": ["nervous-system-reset", "journal"],
  "childhood-emotional-neglect-signs-symptoms-healing": ["inner-child", "journal"],
  "depression-vs-emotional-numbness-difference": ["acts-of-self-care", "somatic-orienting"],
  "fawn-response-people-pleasing-trauma": ["know-your-patterns", "protective-boundary"],
  "generational-trauma": ["journal", "inner-child"],
  "herstel-na-trauma-de-eerste-stap": ["grounding-54321", "nervous-system-reset"],
  "how-long-does-trauma-recovery-take": ["journal", "nervous-system-reset"],
  "how-to-overcome-trauma": ["nervous-system-reset", "grounding-54321"],
  "hyper-independence-after-trauma": ["journal", "secure-within"],
  "intermittent-reinforcement-relationships": ["breaking-free", "trigger-journal"],
  "living-with-ptsd-daily-coping": ["grounding-54321", "extended-exhale"],
  "parentification-when-a-child-becomes-the-parent": ["inner-child", "journal"],
  "phases-of-trauma-recovery": ["safety-stabilisation", "nervous-system-reset"],
  "ptsd-triggers": ["trigger-journal", "feet-on-ground"],
  "signs-of-emotional-trauma-in-adults": ["trigger-journal", "journal"],
  "trauma-responses": ["grounding-54321", "extended-exhale"],
  "walking-on-eggshells-growing-up": ["inner-child", "trigger-journal"],
  "what-depression-feels-like-after-trauma": ["acts-of-self-care", "journal"],
  "what-is-emdr-therapy": ["bilateral", "butterfly-hug"],
  "what-is-emotional-trauma": ["journal", "grounding-54321"],
  "what-is-internal-family-systems-ifs": ["inner-child", "shadow-work"],
  "what-is-ptsd": ["grounding-54321", "trigger-journal"],
  "what-is-trauma": ["grounding-54321", "extended-exhale"],
  "what-is-trauma-healing": ["nervous-system-reset", "journal"],
  "why-is-childhood-trauma-so-hard-to-overcome": ["inner-child", "journal"],

  // CPTSD
  "can-you-recover-from-cptsd": ["nervous-system-reset", "journal"],
  "cptsd-vs-ptsd": ["grounding-54321", "trigger-journal"],
  "eft-tapping-for-emotional-flashbacks": ["tapping", "feet-on-ground"],
  "emotional-flashbacks-cptsd": ["feet-on-ground", "tapping"],
  "emotional-flashbacks-what-they-are-and-how-to-cope": ["feet-on-ground", "extended-exhale"],
  "ptsd-vs-cptsd-differences": ["grounding-54321", "trigger-journal"],
  "signs-of-childhood-trauma-in-adults": ["inner-child", "journal"],
  "what-is-complex-ptsd": ["grounding-54321", "nervous-system-reset"],
  "what-is-cptsd-understanding-complex-ptsd": ["grounding-54321", "nervous-system-reset"],

  // Nervous system
  "depersonalisation-vs-derealisation": ["somatic-orienting", "dissociation-journal"],
  "dissociation-explained-signs-and-what-helps": ["feet-on-ground", "dissociation-journal"],
  "freeze-response-trauma-stuck": ["tre-shake", "somatic-orienting"],
  "how-to-regulate-your-nervous-system-after-trauma": ["voo-breath", "extended-exhale"],
  "how-to-widen-your-window-of-tolerance": ["regulation-compass", "pendulation"],
  "how-trauma-changes-the-brain": ["nervous-system-reset", "extended-exhale"],
  "hypervigilance-why-you-always-feel-on-edge": ["extended-exhale", "somatic-orienting"],
  "je-zenuwstelsel-kalmeren": ["extended-exhale", "voo-breath"],
  "nervous-system-ko-shant-karna": ["extended-exhale", "voo-breath"],
  "trauma-shutdown-freeze-response-depression": ["tre-shake", "acts-of-self-care"],
  "what-is-emotional-regulation": ["regulation-compass", "dbt-skills"],

  // Grounding
  "5-4-3-2-1-grounding-technique-guide": ["grounding-54321", "box-breathing"],
  "50-grounding-techniques-for-anxiety-ptsd-cptsd": ["grounding", "extended-exhale"],
  "aardingsoefeningen-terug-in-het-hier-en-nu": ["grounding-54321", "box-breathing"],
  "grounding-101-when-the-body-activates": ["grounding-54321", "extended-exhale"],
  "zameen-se-judne-ke-abhyas": ["grounding-54321", "box-breathing"],

  // Somatic therapy / EFT
  "does-eft-tapping-work-research": ["tapping", "self-havening"],
  "eft-tapping-points-explained-full-sequence": ["tapping", "eft-flashcards"],
  "what-is-eft-tapping-beginners-guide": ["tapping", "eft-flashcards"],
  "what-is-somatic-therapy": ["voo-breath", "self-havening"],
  "where-is-trauma-stored-in-the-body": ["body-scan", "tre-shake"],

  // Sleep
  "eft-tapping-for-sleep-and-night-anxiety": ["tapping", "meditation-sleep"],
  "trauma-and-sleep-why-its-hard-how-to-rest": ["meditation-sleep", "progressive-relaxation"],

  // Anxiety
  "emotional-dysregulation-how-to-find-calm": ["regulation-compass", "extended-exhale"],
  "trauma-and-anxiety-connection": ["sensory-grounding", "box-breathing"],

  // Relationships
  "coercive-control-the-quiet-kind-of-abuse": ["know-your-patterns", "abuse-journal"],
  "enmeshment-signs-and-recovery": ["protective-boundary", "secure-within"],
  "flying-monkeys-in-narcissistic-abuse": ["abuse-journal", "grounding-54321"],
  "gaslighting-how-to-trust-yourself-again": ["journal", "abuse-journal"],
  "how-to-break-a-trauma-bond": ["breaking-free", "journal"],
  "how-to-help-someone-overcome-trauma": ["extended-exhale", "acts-of-self-care"],
  "how-to-overcome-betrayal-trauma": ["breaking-free", "journal"],
  "how-trauma-affects-relationships": ["secure-within", "journal"],
  "love-bombing-the-first-red-flag": ["know-your-patterns", "journal"],
  "narcissistic-abuse-recognising-the-patterns": ["know-your-patterns", "abuse-journal"],
  "no-contact-vs-grey-rock": ["breaking-free", "container"],
  "the-cycle-of-abuse-explained": ["know-your-patterns", "abuse-journal"],
  "trauma-bonding-explained": ["breaking-free", "journal"],
  "what-is-emotional-abuse-signs-examples": ["abuse-journal", "know-your-patterns"],
  "why-its-so-hard-to-leave-an-abusive-relationship": ["breaking-free", "journal"],
  "why-you-miss-them-withdrawal-after-trauma-bonding": ["breaking-free", "feet-on-ground"],

  // Attachment
  "fear-of-abandonment-after-trauma": ["secure-within", "hand-on-heart"],
  "how-to-heal-from-childhood-trauma-as-an-adult": ["inner-child", "journal"],
  "trauma-bonding-with-a-parent": ["inner-child", "breaking-free"],

  // Self-care
  "behavioural-activation-small-steps-depression": ["acts-of-self-care", "journal"],
  "how-to-stop-people-pleasing": ["know-your-patterns", "protective-boundary"],
  "rebuilding-your-sense-of-self-after-abuse": ["self-love", "journal"],
  "self-abandonment-how-to-come-back-to-yourself": ["self-love", "inner-child"],
  "trauma-informed-self-care-daily-practice": ["acts-of-self-care", "hand-on-heart"],
  "trauma-journal-how-to-start": ["journal", "trigger-journal"],

};

/** Fallback pairing by category, so new articles are covered automatically. */
export const CATEGORY_PRACTICE_IDS: Record<string, PracticeId[]> = {
  "trauma-recovery": ["grounding-54321", "journal"],
  cptsd: ["grounding-54321", "nervous-system-reset"],
  "nervous-system": ["voo-breath", "extended-exhale"],
  grounding: ["grounding-54321", "box-breathing"],
  "somatic-therapy": ["self-havening", "tapping"],
  sleep: ["meditation-sleep", "progressive-relaxation"],
  anxiety: ["sensory-grounding", "extended-exhale"],
  relationships: ["journal", "know-your-patterns"],
  attachment: ["secure-within", "inner-child"],
  "self-care": ["acts-of-self-care", "hand-on-heart"],
};

const DEFAULT_PRACTICE_IDS: PracticeId[] = ["grounding-54321", "journal"];


/**
 * Always returns at least two practices for an article. Explicit mapping first,
 * then the category pairing, then a safe default.
 */
export function practicesForArticle(slug: string, category?: string): ArticlePractice[] {
  const ids =
    ARTICLE_PRACTICE_IDS[slug] ??
    (category ? CATEGORY_PRACTICE_IDS[category] : undefined) ??
    DEFAULT_PRACTICE_IDS;

  const unique = Array.from(new Set(ids)).filter((id) => id in PRACTICE_LIBRARY);
  const filled = unique.length >= 2 ? unique : [...unique, ...DEFAULT_PRACTICE_IDS.filter((d) => !unique.includes(d))];
  return Array.from(new Set(filled)).slice(0, 3).map((id) => PRACTICE_LIBRARY[id]);
}

/** Kept for older callers expecting a single practice. */
export function practiceForArticle(slug: string, category?: string): ArticlePractice | undefined {
  return practicesForArticle(slug, category)[0];
}
